/* eslint-disable no-unreachable */
/* eslint-disable import/no-cycle */
import { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import { subscriptionTypes } from '@constants/index';
import Image from '@base/FutureImage';
import usePricings from '@cont/Checkout/usePricings';
import LeftChevron from 'aquilla/icons/actions/LeftChevron';
import { useRouter } from 'next/router';
import usePurchaseDetails from '@cont/Checkout/usePurchaseDetails';
import useFetch from '@hooks/useFetch';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsSubscribedToGoal } from '@api/selectors/subscription';
import { CHECKOUT_PROCEED_TO_PAYMENT_SELECTED } from '@cont/Checkout/constants/segment';
import { segment } from '@actions/analytics';
import pushRoute from '@utils/pushRoute';
import useCheckoutEventProps from '@hooks/useCheckoutEventProps';
import Typography from 'aquilla/core/atoms/Typography';
import { getMe } from '@api/selectors/auth';
import { plusProceedPaymentEventAnalytics } from '@cont/Checkout/utils';
import isEmptyObject from '@utils/isEmptyObject';
import UpgradePlanSelectionPage from './UpgradePlanSelectionPage';
import UpgradePreCheckoutPage from './UpgradePreCheckoutPage';
import {
  HeaderWrapper,
  TopWrapper,
  HelpWrapper,
  isUpgradeRefundApplicable
} from '../PlanSelection';
import {
  defaultSelectedPricing,
  discountedPricingIfAvailable
} from '../planSelectionHelper';

const Loader = styled(CircularLoader)`
  margin: auto;
`;

const LeftChevronWrapper = styled.div``;

const Title = styled(Typography)`
  width: 300px;
`;

export const UPGRADESELECTIONSTEPS = {
  PLANSELECTION: 1,
  PRECHECKOUT: 2
};

const UpgradePlanSelectionFlow = ({
  setIsHelpDrawerOpen,
  setFlowSteps: setBaseFlowSteps
}) => {
  const { query } = useRouter();
  const { isLoading: isMeDataLoading, isValidating: isMeDataValidating } =
    useMyInfo();
  const me = useMyInfo(getMe);

  const [flowSteps, setFlowSteps] = useState(
    UPGRADESELECTIONSTEPS?.PLANSELECTION
  );
  const [selectedPlan, setSelectedPlan] = useState<
    Record<string, any> | undefined
  >();

  const [previousStep, setPreviousStep] = useState<number>();
  const [referralCode, setReferralCode] = useState<string>();
  const [preFilteredSubscriptionLists, setPreFilteredSubscriptionLists] =
    useState<Record<string, any> | undefined>();

  const {
    query: { goalUID = '', referral_code: eventReferralCode = '' } = {}
  } = useRouter();

  const { pricings, isLoading: pricingLoading } = usePricings({ goalUID });

  const { goal = {} } = pricings ?? {};

  const isPlusUser = useMyInfo(getIsSubscribedToGoal(goalUID));

  const { tax_breakdown = [] } = selectedPlan ?? {};

  const {
    purchaseDetails,
    purchaseDetailsWithoutDiscount,
    referral_code,
    validateReferralInProgress,
    referralError,
    isError
  } = usePurchaseDetails({ subscriptionUid: selectedPlan?.uid, referralCode });

  const {
    data: discountedPricings,
    isError: referralAllError,
    isLoading: referralAllLoading
  } = useFetch(
    referral_code && goal?.uid
      ? `v2/subscriptions/validate_referral_all_subscriptions/?referral=${encodeURIComponent(
          referralCode
        )}&goal=${goal?.uid}&iconic=${
          goal?.is_iconic_available
        }&upgrade=true&scheme=2`
      : null,
    { useCamelCase: false },
    { shouldRetryOnError: false }
  );

  useEffect(() => {
    const preFilteredSubscriptionLists = pricings?.subscriptions
      ? [...pricings.subscriptions].filter(
          (pricing) =>
            subscriptionTypes.ICONIC === pricing.type &&
            !!pricing?.is_purchase_allowed
        )
      : [];
    setPreFilteredSubscriptionLists(
      pricingLoading ? null : preFilteredSubscriptionLists
    );
  }, [isPlusUser, pricingLoading, pricings]);

  const preFilteredUpgradedSubscriptionLists = useMemo(() => {
    const allowedPreFilteredSubscriptionLists =
      preFilteredSubscriptionLists?.filter((plan) => {
        return !!plan?.is_purchase_allowed;
      });
    return allowedPreFilteredSubscriptionLists
      ? allowedPreFilteredSubscriptionLists?.reverse()
      : null;
  }, [preFilteredSubscriptionLists]);

  const discountedPricingLists = useMemo(() => {
    return discountedPricings
      ? discountedPricings.reduce((acc, pricing) => {
          const newPricing = {
            ...pricing?.subscription,
            referral_details: { ...pricing?.referral }
          };
          if (pricing?.subscription?.type === subscriptionTypes.ICONIC) {
            acc.push(newPricing);
          }
          return acc;
        }, [])
      : [];
  }, [discountedPricings]);

  const referralValid = !referralAllLoading && !referralAllError;

  useEffect(() => {
    if (referralCode && referralValid)
      setPreFilteredSubscriptionLists(discountedPricingLists);
  }, [
    discountedPricingLists,
    referralCode,
    referralValid,
    referralAllError,
    referralAllLoading
  ]);

  const isReferralAdded = referralValid && !!referralCode;

  const eventProps = useCheckoutEventProps(
    goalUID,
    discountedPricingIfAvailable(
      selectedPlan?.uid,
      isReferralAdded,
      discountedPricings
    ) || defaultSelectedPricing(selectedPlan?.uid, pricings),
    false
  );

  const subscription_price =
    tax_breakdown?.length > 0
      ? tax_breakdown?.find((tax) => {
          return tax?.label === 'Total';
        })
      : null;

  const evtPayload = {
    ...eventProps,
    referral_code: eventReferralCode || 'NA',
    is_referral_successful: Boolean(eventReferralCode) && !referralError,
    subscription_price: Number(subscription_price?.amount)
  };

  const isMeDataProcessing = isMeDataLoading && isMeDataValidating;

  useEffect(() => {
    if (!isMeDataProcessing)
      plusProceedPaymentEventAnalytics({
        ...evtPayload,
        ...me,
        userId: me?.uid,
        ...goal,
        user_auth: !isEmptyObject(me)
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMeDataProcessing]);

  const handleUpgradePlanSubmit = (plan) => {
    segment.track(CHECKOUT_PROCEED_TO_PAYMENT_SELECTED, evtPayload);
    pushRoute({
      pathname: '/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]',
      query: {
        ...query,
        subscriptionUID: plan.uid
      }
    });
  };

  const handlePlanSelection = (subscription) => {
    const { upgrade_refund } = subscription;
    if (upgrade_refund) {
      setSelectedPlan(subscription);
      setPreviousStep(UPGRADESELECTIONSTEPS.PLANSELECTION);
      setFlowSteps(UPGRADESELECTIONSTEPS.PRECHECKOUT);
    } else {
      handleUpgradePlanSubmit(subscription);
    }
  };

  const showBackButton = previousStep && flowSteps !== previousStep;

  const RenderUpgradeComponent = () => {
    if (flowSteps) {
      switch (flowSteps) {
        case UPGRADESELECTIONSTEPS.PRECHECKOUT:
          return (
            <UpgradePreCheckoutPage
              setFlowSteps={setFlowSteps}
              selectedPlan={selectedPlan}
              goalDetails={goal}
              setPreviousStep={setPreviousStep}
              onSubmit={handleUpgradePlanSubmit}
            />
          );
        case UPGRADESELECTIONSTEPS.PLANSELECTION:
          return preFilteredUpgradedSubscriptionLists ? (
            <UpgradePlanSelectionPage
              onSubmit={(subscription) => {
                handlePlanSelection(subscription);
              }}
              goalDetails={goal}
              pricings={pricings}
              setPreviousStep={setPreviousStep}
              setBaseFlowSteps={setBaseFlowSteps}
              pricingLoading={pricingLoading}
              setReferralCode={setReferralCode}
              referralCode={referralCode}
              selectedPlan={selectedPlan}
              setSelectedPlan={setSelectedPlan}
              purchaseDetails={purchaseDetails}
              referralError={referralError}
              validateReferralInProgress={validateReferralInProgress}
              preFilteredSubscriptionLists={
                preFilteredUpgradedSubscriptionLists
              }
              referralValid={referralValid}
              referralAllLoading={referralAllLoading}
              discountedPricings={discountedPricings}
              showBackButton={showBackButton}
            />
          ) : (
            <></>
          );
        default:
          return (
            <UpgradePlanSelectionPage
              onSubmit={(subscription) => {
                setSelectedPlan(subscription);
                setFlowSteps(UPGRADESELECTIONSTEPS.PRECHECKOUT);
              }}
              goalDetails={goal}
              pricings={pricings}
              setPreviousStep={setPreviousStep}
              setBaseFlowSteps={setBaseFlowSteps}
              pricingLoading={pricingLoading}
              setReferralCode={setReferralCode}
              referralCode={referralCode}
              selectedPlan={selectedPlan}
              setSelectedPlan={setSelectedPlan}
              purchaseDetails={purchaseDetails}
              referralError={referralError}
              validateReferralInProgress={validateReferralInProgress}
              preFilteredSubscriptionLists={
                preFilteredUpgradedSubscriptionLists
              }
              referralValid={referralValid}
              referralAllLoading={referralAllLoading}
              discountedPricings={discountedPricings}
              showBackButton={showBackButton}
            />
          );
      }
    } else {
      return <Loader color="var(--color-i-black)" />;
    }
  };

  return (
    <>
      <HeaderWrapper>
        <TopWrapper $showBackButton={showBackButton}>
          {showBackButton ? (
            <LeftChevronWrapper
              onClick={() => {
                setFlowSteps(previousStep);
              }}
            >
              <LeftChevron height={24} width={24} />
            </LeftChevronWrapper>
          ) : (
            <Title variant="h2">
              Extend duration for your {goal?.name} with{' '}
              <span style={{ color: 'var(--color-i-orange)' }}>Iconic</span>
            </Title>
          )}
          <HelpWrapper
            onClick={() => {
              setIsHelpDrawerOpen(true);
            }}
            $showBackButton={showBackButton}
          >
            <Image
              src="newPlanSelection/callfilledIcon.svg"
              width={16}
              height={16}
              alt="call icon"
            />
            Help
          </HelpWrapper>
        </TopWrapper>
      </HeaderWrapper>
      {RenderUpgradeComponent()}
    </>
  );
};

export default UpgradePlanSelectionFlow;
